/**
 * Persist tournament self-registration intent across Create account / Google auth
 * so new users return to the tournament (or its register redirect) instead of Me edit.
 */

const PENDING_KEY = "tra_pending_tournament_registration_v1";

/**
 * `/browse/:id`, `/browse/:id/register`, optional `?claim=`, or a bare competition id.
 * @param {string} raw
 * @returns {{ competitionId: string, targetRoute: string } | null}
 */
export function parseTournamentRegistrationTarget(raw) {
  const text = String(raw || "").trim();
  if (!text) return null;
  if (!text.startsWith("/")) {
    const competitionId = text;
    return {
      competitionId,
      targetRoute: `/browse/${encodeURIComponent(competitionId)}/register`,
    };
  }
  const hashAt = text.indexOf("#");
  const withoutHash = hashAt >= 0 ? text.slice(0, hashAt) : text;
  const queryAt = withoutHash.indexOf("?");
  const path = queryAt >= 0 ? withoutHash.slice(0, queryAt) : withoutHash;
  const search = queryAt >= 0 ? withoutHash.slice(queryAt) : "";
  const match = path.match(/^\/browse\/([^/]+)(?:\/register)?\/?$/);
  if (!match) return null;
  const competitionId = decodeURIComponent(match[1] || "").trim();
  if (!competitionId) return null;
  const suffix = /\/register\/?$/.test(path) ? "/register" : "";
  return {
    competitionId,
    targetRoute: `/browse/${encodeURIComponent(competitionId)}${suffix}${search}`,
  };
}

/**
 * @param {string | null | undefined} pathOrCompetitionId  browse path or competition id
 */
export function writePendingTournamentRegistration(pathOrCompetitionId) {
  const raw = String(pathOrCompetitionId || "").trim();
  if (!raw) {
    clearPendingTournamentRegistration();
    return null;
  }
  const parsed = parseTournamentRegistrationTarget(raw);
  const competitionId = parsed?.competitionId || null;
  const targetRoute = parsed?.targetRoute || null;
  if (!parsed && raw.startsWith("/")) return null;
  if (!competitionId || !targetRoute) {
    clearPendingTournamentRegistration();
    return null;
  }
  const record = {
    competitionId,
    targetRoute,
    savedAtMs: Date.now(),
  };
  try {
    sessionStorage.setItem(PENDING_KEY, JSON.stringify(record));
  } catch {
    /* private mode */
  }
  return record;
}

export function readPendingTournamentRegistration() {
  try {
    const raw = sessionStorage.getItem(PENDING_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    const competitionId = String(parsed.competitionId || "").trim();
    const targetRoute = String(parsed.targetRoute || "").trim();
    if (!competitionId && !targetRoute) return null;
    return {
      competitionId: competitionId || null,
      targetRoute:
        targetRoute ||
        (competitionId ? `/browse/${encodeURIComponent(competitionId)}/register` : null),
      savedAtMs: Number(parsed.savedAtMs) || null,
    };
  } catch {
    return null;
  }
}

export function clearPendingTournamentRegistration() {
  try {
    sessionStorage.removeItem(PENDING_KEY);
  } catch {
    /* private mode */
  }
}

export function hasPendingTournamentRegistration() {
  const p = readPendingTournamentRegistration();
  return Boolean(p?.targetRoute || p?.competitionId);
}

/**
 * @param {Pick<Location, "pathname"> | string | null | undefined} loc
 */
export function isTournamentRegisterPath(loc) {
  const pathname = typeof loc === "string" ? loc : loc?.pathname || "";
  return /^\/browse\/[^/]+(?:\/register)?\/?$/.test(pathname);
}

/**
 * @param {Pick<Location, "pathname"> | string | null | undefined} loc
 */
export function captureTournamentRegistrationFromLocation(loc) {
  const pathname = typeof loc === "string" ? loc : loc?.pathname || "";
  if (!isTournamentRegisterPath(pathname)) return null;
  return writePendingTournamentRegistration(pathname);
}
